import { useState, useEffect } from 'react';
import { X, AlertTriangle, Briefcase, Calendar, Loader, XCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from 'axios';
import { toast } from 'sonner';

const CancelUnhiredPositionModal = ({ job, position, crewMemberIndex, userEmail, isOpen, onClose, onSuccess }) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCancellationReason('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !job || !position) return null;

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleConfirmCancel = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${job.id}/cancel-position`,
        {
          crew_member_index: crewMemberIndex,
          cancellation_reason: cancellationReason.trim(),
          user_email: userEmail
        }
      );

      if (response.data.success) {
        toast.success(`${response.data.cancelled_role} position cancelled.`);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Cancel position error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel position');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[200]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', padding: 20, fontFamily: 'var(--sz-font-body)' }}
    >
      <div
        className="w-full max-w-lg flex flex-col"
        style={{
          maxHeight: '90vh',
          overflow: 'hidden',
          background: '#12162A',
          border: '1px solid rgba(185,28,28,.3)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
        }}
      >
        <div
          className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(120deg,#B91C1C,#7F1D1D)' }}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h3 style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>Cancel Position</h3>
          </div>
          <button
            onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: '0 0 auto' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div style={{ borderRadius: 12, padding: 16, background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.1)' }}>
            <h4 className="mb-3 flex items-center gap-2" style={{ fontWeight: 600, color: '#fff' }}>
              <XCircle className="w-4 h-4" style={{ color: '#F87171' }} />
              Position Being Cancelled
            </h4>
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <Badge className="text-sm px-2 py-1" style={{ background: 'rgba(120,40,124,.25)', color: '#C98AD4' }}>
                  {position.role}
                </Badge>
                <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,.5)' }}>
                  {position.hours || 0} hrs · {formatCurrency(position.ratePerHour)}/hr
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.5)' }}>This position is currently unhired.</p>
              </div>
              <div className="text-right">
                <p className="text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>Position Total</p>
                <p className="text-xl font-bold" style={{ color: '#F87171', fontFamily: 'var(--sz-font-mono)' }}>{formatCurrency(position.subtotal)}</p>
              </div>
            </div>
          </div>

          <div style={{ borderRadius: 12, padding: 12, background: 'rgba(37,99,235,.1)', border: '1px solid rgba(37,99,235,.25)' }}>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" style={{ color: '#60A5FA' }} />
                <span className="truncate" style={{ color: 'rgba(255,255,255,.7)' }}>{job.title}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" style={{ color: '#60A5FA' }} />
                <span style={{ color: 'rgba(255,255,255,.7)' }}>
                  {job.jobDate ? new Date(job.jobDate).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  }) : 'TBD'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm" style={{ fontWeight: 600, color: '#fff' }}>
              Reason for Cancellation <span style={{ color: '#F87171' }}>*</span>
            </label>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Please explain why this position is being cancelled..."
              className="w-full resize-none text-sm"
              style={{ padding: '12px 14px', borderRadius: 11, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff', outline: 'none', fontFamily: 'var(--sz-font-body)' }}
              rows={3}
            />
          </div>

          <div style={{ borderRadius: 12, padding: 16, background: 'rgba(185,28,28,.1)', border: '1px solid rgba(185,28,28,.4)' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F87171' }} />
              <div>
                <p style={{ fontWeight: 600, color: '#F87171' }}>This action cannot be undone</p>
                <ul className="text-sm mt-1 list-disc list-inside" style={{ color: 'rgba(255,255,255,.7)' }}>
                  <li>This position will be removed from the job</li>
                  <li>Any other hired crew members are not affected</li>
                  <li>If this is the last open position and others are hired, the job will move to HIRED</li>
                  <li>If a creative has a pending offer for this role, decline or withdraw it first</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div
          className="p-4 flex-shrink-0 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.02)' }}
        >
          <Button
            onClick={onClose}
            variant="outline"
            disabled={isSubmitting}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
          >
            Go Back
          </Button>
          <Button
            onClick={handleConfirmCancel}
            disabled={!cancellationReason.trim() || isSubmitting}
            style={{ background: '#B91C1C', color: '#fff', border: 'none' }}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Cancel Position
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancelUnhiredPositionModal;
